import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Table,
  TableProps,
  Select,
  Row,
  Col,
  Button,
  PaginationProps,
  Alert,
  Typography,
  Modal,
  Input,
  Form,
  Tag,
  Tooltip,
  Checkbox,
  Space,
} from "antd";
import { getRequest, ResponseDataType, postRequest, postDirect } from "api";
import { toast } from "react-toastify";
import { BillInfo } from "Pages/Bill/Information";
import { EllipsisTooltip } from "./EllipsisTooltip";
import { ClassLevelSelect } from 'Components';
import {
  SearchOutlined,
  CalendarOutlined,
  SyncOutlined,
  SafetyCertificateOutlined,
  SafetyCertificateFilled,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { getStoredWorkMonth } from 'utils/workMonthStorage';
import {
  StudentBillGroup,
  groupBillsByStudent,
  getGroupSelectionState,
  toggleGroupSelectionKeys,
  toggleSingleBillKey,
} from "./studentBillGrouping";
import "./UnpaidBillList.css";

const { Option } = Select;

interface unPaidBillDataType {
  invoidId: string;
  billId?: string;
  studentInvoiceId: number;
  studentId: number;
  studentName: string;
  className: string;
  createdDate: number;
  parentPhonenum: string;
  cost: number;
  content: string;
  description: string;
  status: number;
  paymentDate: string | null;
  updatedDate: number;
  schoolYear: string;
  month: string;
  year: string;
  studentCode: string;
  classYearName: string;
  invoiceStatus: number;
  transactionCode: string;
  exportDate?: string | null;
  isEinvoice?: boolean;
}

interface NotificationDataType {
  status: number;
}

interface IncomeSchoolOption {
  id: number;
  name: string;
  code?: string;
  isEinvoice?: boolean;
}

const formatCurrency = (value: number): string => {
  const formattedValue = new Intl.NumberFormat("vi-VN").format(value);
  return `${formattedValue} VNĐ`;
};

const formatCombinedData = (
  month: string,
  year: string,
  schoolYear: string,
): string => {
  const formattedMonth = month.padStart(2, "0");
  return `T${formattedMonth}-${year}-${schoolYear}`;
};

const EXPORTABLE_INVOICE_STATUSES = [0, 1, 4];
const ISSUED_INVOICE_MIN_STATUS = 5;
const XBILL_SUCCESS_CODE = "01";
const BUYER_NAME_PREFIX = "BÁN CHO NGƯỜI TIÊU DÙNG";
const DEFAULT_XBILL_PAYLOAD = {
  mddkdoanh: "00999",
  tddkdoanh: "Ho kinh doanh CYBERLOTUS 999",
  dcddkdoanh: "1A Nguyen Cong Tru, Phường Hai Bà Trưng, Thành Phố Hà Nội",
  invoiceType: "2",
  symbol: "C26TCB",
  paymentMethod: 3,
  bankAccount: "12345689",
  creator: "ktt",
  taxCode: "10",
  itemTaxCode: "-1",
  currencyUnit: "VND",
  defaultItemName: "Dịch vụ",
};

export const PaidBillList = () => {
  const [unPaidBillList, setUnPaidBillList] = useState<unPaidBillDataType[]>(
    [],
  );
  const [selectedYear, setSelectedYear] = useState<number>(
    () => getStoredWorkMonth().year,
  );
  const [selectedMonth, setSelectedMonth] = useState<number>(
    () => getStoredWorkMonth().month,
  );
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [selectedClass, setSelectedClass] = useState<number | undefined>();
  const [selectedClassLevel, setSelectedClassLevel] = useState<string | undefined>(undefined);
  const [classList, setClassList] = useState<any[]>([]);
  const [incomeSchoolList, setIncomeSchoolList] = useState<IncomeSchoolOption[]>([]);
  const [selectedIncomeSchoolId, setSelectedIncomeSchoolId] = useState<number | undefined>(undefined);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [loginForm] = Form.useForm();
  const [loadingLogin, setLoadingLogin] = useState(false);
  const [billConfig, setBillConfig] = useState<any>(null);
  const [remainingInvoiceCount, setRemainingInvoiceCount] = useState<
    number | null
  >(null);
  const [totalInvoiceCount, setTotalInvoiceCount] = useState<number | null>(null);
  const [pendingActionType, setPendingActionType] = useState<
    "EXPORT" | "CHECK" | null
  >(null);
  const [processingRowKeys, setProcessingRowKeys] = useState<React.Key[]>([]);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isConfirmIssueModalOpen, setIsConfirmIssueModalOpen] = useState(false);
  const [isConsolidatedModalOpen, setIsConsolidatedModalOpen] = useState(false);
  const [isConsolidating, setIsConsolidating] = useState(false);
  const [consolidateProgress, setConsolidateProgress] = useState<{ current: number; total: number } | null>(null);
  const [selectedStudentInvoiceId, setSelectedStudentInvoiceId] = useState<number | undefined>();
  const [hasChanged, setHasChanged] = useState(false);
  const [searchStudentName, setSearchStudentName] = useState<string>('');
  const [debouncedStudentName, setDebouncedStudentName] = useState<string>('');
  // Bộ lọc HĐĐT: 'einvoice' (Cần xuất HĐĐT - Mặc định), 'no_einvoice' (Không xuất HĐĐT), 'all' (Tất cả)
  const [einvoiceFilter, setEinvoiceFilter] = useState<'einvoice' | 'no_einvoice' | 'all'>('einvoice');
  // Bộ lọc Trạng thái HĐ: undefined (Tất cả), 0..9 (Mã trạng thái chi tiết)
  const [selectedInvoiceStatus, setSelectedInvoiceStatus] = useState<number | undefined>(undefined);
  const [loadingTable, setLoadingTable] = useState<boolean>(false);

  const filteredIncomeSchools = useMemo(() => {
    if (einvoiceFilter === 'einvoice') {
      return incomeSchoolList.filter((src) => Boolean(src.isEinvoice) === true);
    }
    if (einvoiceFilter === 'no_einvoice') {
      return incomeSchoolList.filter((src) => !src.isEinvoice);
    }
    return incomeSchoolList;
  }, [incomeSchoolList, einvoiceFilter]);

  const years = useMemo(() => {
    const startYear = 2021;
    const endYear = Math.max(new Date().getFullYear() + 2, selectedYear + 1);
    const list: number[] = [];
    for (let y = startYear; y <= endYear; y++) {
      list.push(y);
    }
    return list;
  }, [selectedYear]);
  const months = [
    { label: "Tháng 1", value: 1 },
    { label: "Tháng 2", value: 2 },
    { label: "Tháng 3", value: 3 },
    { label: "Tháng 4", value: 4 },
    { label: "Tháng 5", value: 5 },
    { label: "Tháng 6", value: 6 },
    { label: "Tháng 7", value: 7 },
    { label: "Tháng 8", value: 8 },
    { label: "Tháng 9", value: 9 },
    { label: "Tháng 10", value: 10 },
    { label: "Tháng 11", value: 11 },
    { label: "Tháng 12", value: 12 },
  ];
  const schoolId = localStorage.getItem("schoolId");

  const fetchUnpaidBills = async () => {
    setLoadingTable(true);
    try {
      const queryParams: Record<string, string> = {
        page: currentPage.toString(),
        perPage: pageSize.toString(),
        year: selectedYear.toString(),
        month: selectedMonth.toString(),
        schoolId: schoolId || "",
        status: "1",
        groupByStudent: "true",
      };
      if (selectedClass) {
        queryParams['classId'] = selectedClass.toString();
      } else if (selectedClassLevel) {
        queryParams['classLevel'] = selectedClassLevel;
      }
      if (debouncedStudentName) {
        queryParams['studentName'] = debouncedStudentName;
      }
      if (einvoiceFilter === 'einvoice') {
        queryParams['isEinvoice'] = 'true';
      } else if (einvoiceFilter === 'no_einvoice') {
        queryParams['isEinvoice'] = 'false';
      }
      if (selectedInvoiceStatus !== undefined && selectedInvoiceStatus !== null && einvoiceFilter !== 'no_einvoice') {
        queryParams['invoiceStatus'] = selectedInvoiceStatus.toString();
      }
      if (selectedIncomeSchoolId !== undefined && selectedIncomeSchoolId !== null) {
        queryParams['incomeSchoolId'] = selectedIncomeSchoolId.toString();
      }
      const queryString = new URLSearchParams(queryParams).toString();

      const url = `/cms/invoice/list-invoices?${queryString}`;
      const response: ResponseDataType<{
        list: unPaidBillDataType[];
        total: number;
      }> = await getRequest(url);

      if (response?.code === 200) {
        setUnPaidBillList(response.info?.list || []);
        setTotalItems(response.info?.total || 0);
      } else {
        setUnPaidBillList([]);
        setTotalItems(0);
        console.log(response?.msg || "Lỗi không xác định");
      }
    } catch (error) {
      console.error("Error fetching bills:", error);
      setUnPaidBillList([]);
      setTotalItems(0);
    } finally {
      setLoadingTable(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedStudentName(searchStudentName.trim());
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchStudentName]);

  const handlePressEnter = () => {
    setDebouncedStudentName(searchStudentName.trim());
    setCurrentPage(1);
  };

  // Lắng nghe thay đổi Kỳ làm việc từ cấu hình / thanh Header
  useEffect(() => {
    const handleWorkMonthChange = (e: any) => {
      if (e.detail) {
        setSelectedMonth(e.detail.month);
        setSelectedYear(e.detail.year);
        setCurrentPage(1);
      }
    };
    window.addEventListener('workMonthChanged', handleWorkMonthChange);
    return () => window.removeEventListener('workMonthChanged', handleWorkMonthChange);
  }, []);

  useEffect(() => {
    fetchUnpaidBills();
    setSelectedRowKeys([]);
  }, [selectedYear, selectedMonth, currentPage, pageSize, selectedClass, selectedClassLevel, debouncedStudentName, einvoiceFilter, selectedInvoiceStatus, selectedIncomeSchoolId]);

  useEffect(() => {
    const fetchClasses = async () => {
      if (!schoolId) return;
      const url = `/cms/class/get-class-years-by-start-end-schoolId?schoolId=${schoolId}&start=${selectedYear}&page=1&perPage=1000`;
      const res: ResponseDataType<any> = await getRequest(url);
      if (res?.code === 200) {
        setClassList(res?.info?.list || []);
      }
    };
    fetchClasses();
  }, [schoolId, selectedYear]);

  useEffect(() => {
    const fetchIncomeSchools = async () => {
      if (!schoolId) return;
      try {
        const res: ResponseDataType<{ list: IncomeSchoolOption[] }> = await getRequest(
          `/cms/accountant/income-school?schoolId=${schoolId}&page=1&perPage=200`,
        );
        if (res?.code === 200) {
          setIncomeSchoolList(res?.info?.list || []);
        }
      } catch (error) {
        console.error('Error fetching income schools:', error);
      }
    };
    fetchIncomeSchools();
  }, [schoolId]);

  // Fetch bill config và invoice summary để hiển thị số hóa đơn còn lại
  const fetchBillData = async () => {
    if (!schoolId) return;
    try {
      // 1. Lấy config để lấy symbol
      const configRes = await getRequest(
        `/cms/school-bill-config?schoolId=${schoolId}`,
      );
      if (configRes?.code === 200 && configRes.info) {
        setBillConfig(configRes.info);

        // 2. Lấy số lượng hóa đơn còn lại
        if (configRes.info.symbol) {
          const summaryRes = await getRequest(
            `/cms/school-bill-config/invoice-usage-summary?schoolId=${schoolId}&year=${selectedYear}&invoiceSymbol=${configRes.info.symbol}`,
          );
          if (summaryRes?.code === 200 && summaryRes.info) {
            setRemainingInvoiceCount(summaryRes.info.remainingQuantity);
            setTotalInvoiceCount(summaryRes.info.totalPurchased);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching bill data:", error);
    }
  };

  useEffect(() => {
    fetchBillData();
  }, [schoolId, selectedYear]);

  const handlePageChange: PaginationProps["onChange"] = (page, pageSize) => {
    setCurrentPage(page);
    setPageSize(pageSize || 10);
  };

  // const handleNotifyClick = async () => {
  //   const schoolidNumber = Number(localStorage.getItem('schoolId'));
  //   const formData = {
  //     title: 'Thông báo',
  //     message: 'Thông báo học phí',
  //     schoolId: schoolidNumber,
  //   };
  //   const response: ResponseDataType<NotificationDataType> = await postRequest(
  //     '/cms/invoice/send-notification-invoice',
  //     formData,
  //     'json'
  //   );

  //   if (response?.code === 200) {
  //     toast.success('Gửi thông báo thành công!');
  //   } else {
  //     toast.error('Gửi thông báo thất bại!');
  //   }
  // };

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

  const isBillSelectable = useCallback(
    (record: unPaidBillDataType) =>
      !processingRowKeys.includes(record.studentInvoiceId) && record.isEinvoice !== false,
    [processingRowKeys]
  );

  const studentGroups = useMemo(
    () => groupBillsByStudent(unPaidBillList),
    [unPaidBillList]
  );

  const multiBillGroupKeys = useMemo(
    () => studentGroups.filter((g) => g.billCount > 1).map((g) => g.groupKey),
    [studentGroups]
  );

  useEffect(() => {
    setExpandedRowKeys((prev) =>
      prev.filter((key) => multiBillGroupKeys.includes(String(key)))
    );
  }, [multiBillGroupKeys]);

  const isAllExpanded =
    multiBillGroupKeys.length > 0 &&
    multiBillGroupKeys.every((key) => expandedRowKeys.includes(key));

  const handleToggleExpandAll = () => {
    if (isAllExpanded) {
      setExpandedRowKeys([]);
    } else {
      setExpandedRowKeys(multiBillGroupKeys);
    }
  };

  const allPageSelectableIds = useMemo(
    () => unPaidBillList.filter(isBillSelectable).map((item) => item.studentInvoiceId),
    [unPaidBillList, isBillSelectable]
  );

  const isPageAllChecked =
    allPageSelectableIds.length > 0 &&
    allPageSelectableIds.every((id) => selectedRowKeys.includes(id));

  const isPageIndeterminate =
    !isPageAllChecked &&
    allPageSelectableIds.some((id) => selectedRowKeys.includes(id));

  const handleToggleSelectAllPage = useCallback(() => {
    if (isPageAllChecked) {
      const pageSet = new Set<React.Key>(allPageSelectableIds);
      setSelectedRowKeys((prev) => prev.filter((k) => !pageSet.has(k)));
    } else {
      const nextSet = new Set<React.Key>(selectedRowKeys);
      for (const id of allPageSelectableIds) {
        nextSet.add(id);
      }
      setSelectedRowKeys(Array.from(nextSet));
    }
  }, [isPageAllChecked, allPageSelectableIds, selectedRowKeys]);

  const totalSelectedAmount = unPaidBillList
    .filter((item) => selectedRowKeys.includes(item.studentInvoiceId))
    .reduce((sum, item) => sum + (item.cost || 0), 0);

  const tinhTongTienCoThue = (tongTien: number, vat: number): number => {
    switch (vat) {
      case -2: // Không kê khai
      case -1: // Không thuế
      case 0: // Thuế 0%
        return tongTien;
      case 5:
        return tongTien * 1.05;
      case 10:
        return tongTien * 1.1;
      case 35: // Thuế 5% x 70%
        return tongTien * (1 + 0.05 * 0.7);
      case 7: // Thuế 10% x 70%
        return tongTien * (1 + 0.1 * 0.7);
      case 8:
        return tongTien * 1.08;
      default:
        return tongTien;
    }
  };

  const handleXBillLogin = async () => {
    try {
      const values = await loginForm.validateFields();
      setLoadingLogin(true);

      const res = await postRequest("/token/2", {
        doanhnghiep_mst: values.mst,
        username: values.username,
        password: values.password,
      });

      const token = res?.info?.result?.access_token;

      if (token) {
        localStorage.setItem("xbill_token", token);
        sessionStorage.setItem("xbill_token", token);
        localStorage.setItem("xbill_status", "LOGGED_IN");
        sessionStorage.setItem("xbill_status", "LOGGED_IN");
        localStorage.setItem("xbill_login_time", new Date().toLocaleString("vi-VN"));
        toast.success("Đăng nhập XBill thành công");
        setIsLoginModalOpen(false);

        if (pendingActionType) {
          handleBulkAction(pendingActionType);
          setPendingActionType(null);
        }
      } else {
        toast.error("Không lấy được token XBill");
      }
    } catch (err) {
      console.error("XBill login error:", err);
      toast.error("Lỗi đăng nhập XBill");
    } finally {
      setLoadingLogin(false);
    }
  };

  useEffect(() => {
    if (isLoginModalOpen && schoolId) {
      const fetchBillConfig = async () => {
        try {
          const res = await getRequest(
            `/cms/school-bill-config?schoolId=${schoolId}`,
          );
          if (res?.code === 200 && res.info) {
            setBillConfig(res.info);
            loginForm.setFieldsValue({
              mst: res.info.msthue,
              username: res.info.account,
              password: res.info.password,
            });
          }
        } catch (error) {
          console.error("Error fetching bill config:", error);
        }
      };
      fetchBillConfig();
    }
  }, [isLoginModalOpen, schoolId, loginForm]);

  const showBulkExportSummary = (
    successCount: number,
    errorCount: number,
    total: number,
  ) => {
    const summaryContent = (
      <div style={{ padding: "8px 0" }}>
        {successCount > 0 && (
          <div
            style={{
              marginBottom: 8,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span style={{ color: "#52c41a", fontWeight: "bold" }}>
              ✓ Phát hành thành công:
            </span>
            <span>{successCount}</span>
          </div>
        )}
        {errorCount > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ color: "#ff4d4f", fontWeight: "bold" }}>
              ✘ Phát hành thất bại:
            </span>
            <span>{errorCount}</span>
          </div>
        )}
        <div
          style={{
            marginTop: 12,
            borderTop: "1px solid #f0f0f0",
            paddingTop: 8,
            fontSize: "12px",
            color: "#8c8c8c",
          }}
        >
          Tổng số hóa đơn đã xử lý: {total}
        </div>
      </div>
    );

    // Trường hợp xử lý 1 hóa đơn
    if (total === 1) {
      if (successCount === 1) toast.success("Phát hành HĐ HSM thành công.");
      else toast.error("Phát hành HĐ HSM thất bại.");
      return;
    }

    // Trường hợp xử lý nhiều hóa đơn
    if (successCount === total) {
      toast.success(`Thành công! Đã phát hành ${successCount} HĐ HSM.`);
    } else if (errorCount === total) {
      toast.error(`Thất bại! Không thể phát hành ${errorCount} HĐ HSM.`);
    } else {
      const hasError = errorCount > 0;
      const toastMethod = hasError ? toast.warning : toast.success;

      toastMethod(
        <div>
          <b style={{ fontSize: "16px" }}>Kết quả phát hành HĐ HSM</b>
          {summaryContent}
        </div>,
        { autoClose: 8000, icon: false },
      );
    }
  };

  const showBulkCheckSummary = (
    successCount: number,
    errorCount: number,
    total: number,
  ) => {
    const summaryContent = (
      <div style={{ padding: "8px 0" }}>
        {successCount > 0 && (
          <div
            style={{
              marginBottom: 8,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span style={{ color: "#1890ff", fontWeight: "bold" }}>
              ✓ Đồng bộ thành công:
            </span>
            <span>{successCount}</span>
          </div>
        )}
        {errorCount > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ color: "#ff4d4f", fontWeight: "bold" }}>
              ✘ Đồng bộ thất bại:
            </span>
            <span>{errorCount}</span>
          </div>
        )}
        <div
          style={{
            marginTop: 12,
            borderTop: "1px solid #f0f0f0",
            paddingTop: 8,
            fontSize: "12px",
            color: "#8c8c8c",
          }}
        >
          Tổng số hóa đơn đã xử lý: {total}
        </div>
      </div>
    );

    // Trường hợp xử lý 1 hóa đơn
    if (total === 1) {
      if (successCount === 1) toast.success("Đồng bộ trạng thái CQT thành công.");
      else toast.error("Đồng bộ trạng thái CQT thất bại.");
      return;
    }

    // Trường hợp xử lý nhiều hóa đơn
    if (successCount === total) {
      toast.success(
        `Thành công! Đã đồng bộ trạng thái cho ${successCount} hóa đơn.`,
      );
    } else if (errorCount === total) {
      toast.error(
        `Thất bại! Cả ${errorCount} hóa đơn đều không thể đồng bộ trạng thái.`,
      );
    } else {
      const hasError = errorCount > 0;
      const toastMethod = hasError ? toast.warning : toast.success;

      toastMethod(
        <div>
          <b style={{ fontSize: "16px" }}>Kết quả đồng bộ trạng thái CQT</b>
          {summaryContent}
        </div>,
        { autoClose: 8000, icon: false },
      );
    }
  };

  const isXBillTokenExpired = (token: string | null): boolean => {
    if (!token) return true;
    try {
      const parts = token.split(".");
      if (parts.length === 3) {
        const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
        const payload = JSON.parse(window.atob(base64));
        if (typeof payload?.exp === "number") {
          return payload.exp * 1000 <= Date.now() + 60 * 1000;
        }
      }
    } catch (e) {
      // Nếu token không phải chuẩn JWT có exp thì kiểm tra tiếp qua phản hồi API
    }
    return false;
  };

  const getXBillToken = async (forceRefresh: boolean = false): Promise<string | null> => {
    const status = localStorage.getItem("xbill_status") || sessionStorage.getItem("xbill_status");
    if (status !== "LOGGED_IN") {
      toast.error("Bạn chưa đăng nhập XBill. Vui lòng vào màn 'Thông tin cấu hình hóa đơn' để đăng nhập trước khi thao tác!");
      return null;
    }

    const currentToken = localStorage.getItem("xbill_token") || sessionStorage.getItem("xbill_token");
    if (currentToken && !forceRefresh && !isXBillTokenExpired(currentToken)) {
      return currentToken;
    }

    let config = billConfig;
    if (!config && schoolId) {
      try {
        const configRes = await getRequest(`/cms/school-bill-config?schoolId=${schoolId}`);
        if (configRes?.code === 200 && configRes.info) {
          config = configRes.info;
          setBillConfig(config);
        }
      } catch (e) {
        console.error("Error loading bill config for silent refresh in PaidBillList:", e);
      }
    }

    if (config?.msthue && config?.account && config?.password) {
      try {
        const res: any = await postRequest("/token/2", {
          doanhnghiep_mst: config.msthue,
          username: config.account,
          password: config.password,
        });
        const newToken = res?.info?.result?.access_token;
        if (newToken) {
          localStorage.setItem("xbill_token", newToken);
          sessionStorage.setItem("xbill_token", newToken);
          localStorage.setItem("xbill_status", "LOGGED_IN");
          sessionStorage.setItem("xbill_status", "LOGGED_IN");
          localStorage.setItem("xbill_login_time", new Date().toLocaleString("vi-VN"));
          return newToken;
        }
      } catch (err) {
        console.error("Silent refresh XBill in PaidBillList failed:", err);
      }
    }

    toast.error("Phiên đăng nhập XBill đã hết hạn. Vui lòng vào màn 'Thông tin cấu hình hóa đơn' để đăng nhập lại.");
    return null;
  };

  const postXBillWithAutoRefresh = async (
    url: string,
    body: any,
    tokenHolder: { current: string }
  ): Promise<any> => {
    let res = await postDirect(
      url,
      body,
      { Authorization: `Bearer ${tokenHolder.current}` },
      "json"
    );

    const errText = `${res?.message || ""} ${res?.msg || ""} ${typeof res?.info === "string" ? res.info : ""}`;
    const isTokenExpiredResponse =
      !res ||
      res?.status === 401 ||
      res?.status === 500 ||
      res?.code === 401 ||
      (res?.code !== 200 && /401|unauthorized|no body|unauthenticated/i.test(errText));

    if (isTokenExpiredResponse) {
      const refreshedToken = await getXBillToken(true);
      if (!refreshedToken) {
        return { _silentRefreshFailed: true };
      }
      tokenHolder.current = refreshedToken;
      res = await postDirect(
        url,
        body,
        { Authorization: `Bearer ${refreshedToken}` },
        "json"
      );
    }

    return res;
  };

  const handleBulkAction = async (actionType: "EXPORT" | "CHECK") => {
    if (selectedRowKeys.length === 0) return;

    const targetBills = unPaidBillList.filter(
      (item) =>
        selectedRowKeys.includes(item.studentInvoiceId) &&
        item.isEinvoice !== false &&
        (actionType === "EXPORT"
          ? EXPORTABLE_INVOICE_STATUSES.includes(item.invoiceStatus || 0)
          : (item.invoiceStatus || 0) >= ISSUED_INVOICE_MIN_STATUS),
    );

    if (targetBills.length === 0) {
      toast.info(
        actionType === "EXPORT"
          ? "Không có hóa đơn nào đủ điều kiện để phát hành."
          : "Không có hóa đơn nào đủ điều kiện để đồng bộ trạng thái CQT.",
      );
      return;
    }

    if (actionType === "EXPORT") {
      if (remainingInvoiceCount === null || remainingInvoiceCount === 0) {
        toast.error("Trường đã hết hóa đơn khả dụng. Vui lòng mua thêm.");
        return;
      }
      if (targetBills.length > remainingInvoiceCount) {
        toast.error(
          `Bạn đang chọn ${targetBills.length} hóa đơn, nhưng chỉ còn lại ${remainingInvoiceCount} hóa đơn khả dụng. Vui lòng bỏ chọn bớt hoặc mua thêm hóa đơn.`,
        );
        return;
      }
    }

    const token = await getXBillToken(false);
    if (!token) return;
    const tokenHolder = { current: token };

    setProcessingRowKeys(targetBills.map((item) => item.studentInvoiceId));
    try {
      let commonPayload = {
        doanhnghiep_mst: "",
        loaihoadon_ma: DEFAULT_XBILL_PAYLOAD.invoiceType,
        mauso: DEFAULT_XBILL_PAYLOAD.invoiceType,
        kyhieu: DEFAULT_XBILL_PAYLOAD.symbol,
      };

      let configData = billConfig;
      if (!configData) {
        try {
          const configRes = await getRequest(
            `/cms/school-bill-config?schoolId=${schoolId}`,
          );
          if (configRes?.code === 200 && configRes.info) {
            configData = configRes.info;
            setBillConfig(configData);
          }
        } catch (e) {
          console.error("Error fetching bill config:", e);
        }
      }

      if (configData) {
        commonPayload = {
          doanhnghiep_mst: configData.msthue,
          loaihoadon_ma: configData.invoiceType?.toString() || DEFAULT_XBILL_PAYLOAD.invoiceType,
          mauso: configData.invoiceType?.toString() || DEFAULT_XBILL_PAYLOAD.invoiceType,
          kyhieu: configData.symbol || DEFAULT_XBILL_PAYLOAD.symbol,
        };
      }

      const actionName =
        actionType === "EXPORT" ? "phát hành HĐ HSM" : "đồng bộ trạng thái CQT";
      toast.info(`Bắt đầu ${actionName} cho ${targetBills.length} hóa đơn...`);
      let successCount = 0;
      let errorCount = 0;
      const checkedInvoiceResultMap = new Map<string, boolean>();

      for (const info of targetBills) {
        try {
          const studentInvoiceId = info.studentInvoiceId;
          let res;

          if (actionType === "EXPORT") {
            // Trạng thái chưa ký (Mới tạo, Chờ ký, Ký lỗi): Gọi Tạo và Ký HSM
            const detailRes = await getRequest(
              `/cms/accountant/income-student-register/group/${studentInvoiceId}`,
            );
            const details = detailRes?.info ?? [];
            const dschitiet = details.map((item: any, index: number) => {
              const soLuong =
                item.actualUnits && item.actualUnits > 0 ? item.actualUnits : 1;
              const tongTien = item.totalAmount ?? ((item.unitPrice ?? 0) * soLuong);
              const dongia = item.unitPrice ?? (soLuong > 0 ? tongTien / soLuong : tongTien);
              return {
                stt: index + 1,
                hanghoa_loai: 1,
                ma: "",
                ten: item.description ?? DEFAULT_XBILL_PAYLOAD.defaultItemName,
                donvitinh: DEFAULT_XBILL_PAYLOAD.currencyUnit,
                soluong: soLuong,
                dongia: dongia,
                tongtien_chuathue: tongTien,
                mathue: DEFAULT_XBILL_PAYLOAD.itemTaxCode,
                tongtien_cothue: tinhTongTienCoThue(tongTien, -1),
                tyletinhthue: 1,
              };
            });

            const now = new Date();
            const ngaylapFull = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")} ${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}:${String(now.getSeconds()).padStart(2, "0")}`;

            const buyerClass = info.classYearName || info.className || "";
            const buyerSchoolYear = info.schoolYear || "";
            const buyerAddress = buyerClass && buyerSchoolYear
              ? `${buyerClass} - ${buyerSchoolYear}`
              : buyerClass || buyerSchoolYear || "";

            const payload = {
              mddkdoanh: configData?.mddkdoanh || DEFAULT_XBILL_PAYLOAD.mddkdoanh,
              tddkdoanh: configData?.tddkdoanh || DEFAULT_XBILL_PAYLOAD.tddkdoanh,
              dcddkdoanh: configData?.dcddkdoanh || DEFAULT_XBILL_PAYLOAD.dcddkdoanh,
              mchang: "",
              tchang: "",
              doanhnghiep_mst: commonPayload.doanhnghiep_mst,
              loaihoadon_ma: commonPayload.loaihoadon_ma,
              mauso: commonPayload.mauso,
              kyhieu: commonPayload.kyhieu,
              sophieu: "",
              ma_tracuu: "",
              ma_hoadon: info.billId || info.invoidId,
              ngaylap: ngaylapFull,
              tct_macoquan: "",
              dnmua_mst: "",
              dnmua_ten: "",
              dnmua_tennguoimua: info.studentName ? `${BUYER_NAME_PREFIX} - ${info.studentName}` : BUYER_NAME_PREFIX,
              dnmua_diachi: buyerAddress,
              dnmua_sdt: "",
              dnmua_email: "",
              dnmua_diachigiaohang: "",
              khachhang_ma: "",
              thanhtoan_phuongthuc: DEFAULT_XBILL_PAYLOAD.paymentMethod,
              thanhtoan_phuongthuc_ten: "",
              thanhtoan_taikhoan: DEFAULT_XBILL_PAYLOAD.bankAccount,
              thanhtoan_nganhang: "",
              tiente_ma: "",
              tygiangoaite: 0,
              thanhtoan_thoihan: "",
              tongtien_chietkhau: 0,
              tongtien_chietkhau_thuongmai: 0,
              ghichu: "",
              tongtien_chuavat: info.cost || 0,
              tienthue: 0,
              tongtien_covat: info.cost || 0,
              nguoilap: DEFAULT_XBILL_PAYLOAD.creator,
              dschitiet,
              dsthuesuat: [
                {
                  mathue: DEFAULT_XBILL_PAYLOAD.taxCode,
                  tongtien_chiuthue: info.cost || 0,
                  tongtien_thue: 0,
                },
              ],
              nghiquyetapdung: 0,
              trinhky: "",
              dnmua_cccd: "",
              hoadonchietkhau: 0,
              sobangke: "",
              ngaybangke: "",
            };

            res = await postXBillWithAutoRefresh("/token/4", payload, tokenHolder);
          } else {
            // Trạng thái >= 5 (Đang gửi TCT, TVAN đã nhận, TCT chấp nhận...): Gọi Check trạng thái
            // Tránh gọi trùng lặp /token/29 nếu nhiều biên lai cùng chung 1 mã HĐĐT gộp (invoidId)
            const checkKey = `${info.invoidId || info.billId}_${info.transactionCode || ""}`;
            if (checkedInvoiceResultMap.has(checkKey)) {
              if (checkedInvoiceResultMap.get(checkKey)) {
                successCount++;
              } else {
                errorCount++;
              }
              continue;
            }
            res = await postXBillWithAutoRefresh(
              "/token/29",
              {
                doanhnghiep_mst: commonPayload.doanhnghiep_mst,
                magiaodich: info.transactionCode,
                ma_hoadon: info.invoidId || info.billId,
              },
              tokenHolder,
            );
          }

          if (res?._silentRefreshFailed) {
            errorCount += targetBills.length - (successCount + errorCount);
            break;
          }

          const maketqua = res?.info?.result?.maketqua;
          const motaketqua = res?.info?.result?.motaketqua || "";
          const isOk = Boolean(
            res?.code === 200 &&
            (maketqua === XBILL_SUCCESS_CODE || res?.msg === "Success")
          );

          if (actionType === "CHECK") {
            const checkKey = `${info.invoidId || info.billId}_${info.transactionCode || ""}`;
            checkedInvoiceResultMap.set(checkKey, isOk);
          }

          if (isOk) {
            successCount++;
          } else {
            errorCount++;
            console.error(
              `Lỗi HD ${info.invoidId}:`,
              motaketqua || res?.msg || "Không tạo được hóa đơn",
            );
          }
        } catch (error) {
          errorCount++;
        }
      }

      if (actionType === "EXPORT") {
        showBulkExportSummary(
          successCount,
          errorCount,
          targetBills.length,
        );
        // Gọi lại API để cập nhật số lượng hóa đơn còn lại sau khi xuất
        fetchBillData();
      } else {
        showBulkCheckSummary(
          successCount,
          errorCount,
          targetBills.length,
        );
      }

      setSelectedRowKeys([]);
      fetchUnpaidBills();
    } finally {
      setProcessingRowKeys([]);
    }
  };

  const handleConsolidatedExport = async () => {
    if (selectedRowKeys.length === 0 || groupedByStudent.length === 0) return;

    const token = await getXBillToken(false);
    if (!token) return;
    const tokenHolder = { current: token };

    let configData = billConfig;
    if (!configData && schoolId) {
      try {
        const configRes = await getRequest(`/cms/school-bill-config?schoolId=${schoolId}`);
        if (configRes?.code === 200 && configRes.info) {
          configData = configRes.info;
          setBillConfig(configData);
        }
      } catch (e) {
        console.error("Error fetching bill config:", e);
      }
    }

    const commonPayload = {
      mddkdoanh: configData?.mddkdoanh || DEFAULT_XBILL_PAYLOAD.mddkdoanh,
      tddkdoanh: configData?.tddkdoanh || DEFAULT_XBILL_PAYLOAD.tddkdoanh,
      dcddkdoanh: configData?.dcddkdoanh || DEFAULT_XBILL_PAYLOAD.dcddkdoanh,
      doanhnghiep_mst: configData?.msthue || "",
      loaihoadon_ma: configData?.invoiceType?.toString() || DEFAULT_XBILL_PAYLOAD.invoiceType,
      mauso: configData?.invoiceType?.toString() || DEFAULT_XBILL_PAYLOAD.invoiceType,
      kyhieu: configData?.symbol || DEFAULT_XBILL_PAYLOAD.symbol,
    };

    const allExportableBillIds = groupedByStudent.flatMap(g => g.bills.map(b => b.studentInvoiceId));
    setProcessingRowKeys(allExportableBillIds);
    setIsConsolidating(true);
    setConsolidateProgress({ current: 0, total: groupedByStudent.length });
    toast.info(`Bắt đầu gộp và xuất HĐĐT cho ${groupedByStudent.length} học sinh (${exportableBillsCount} khoản thu)...`);

    let successCount = 0;
    let errorCount = 0;
    let totalBillsProcessed = 0;

    try {
      for (let i = 0; i < groupedByStudent.length; i++) {
        const group = groupedByStudent[i];
        setConsolidateProgress({ current: i + 1, total: groupedByStudent.length });

        try {
          const primaryBill = group.bills[0];
          const subBillIds = group.bills.map((b: unPaidBillDataType) => b.billId || b.invoidId).filter(Boolean);

          const detailResponses = await Promise.all(
            group.bills.map((bill: unPaidBillDataType) =>
              getRequest(`/cms/accountant/income-student-register/group/${bill.studentInvoiceId}`).catch(err => {
                console.error(`Error fetching detail for bill ${bill.studentInvoiceId}:`, err);
                return null;
              })
            )
          );
          const allDetails: any[] = detailResponses.flatMap(detailRes =>
            detailRes?.info && Array.isArray(detailRes.info) ? detailRes.info : []
          );

          const dschitiet = allDetails.map((item: any, index: number) => {
            const soLuong = item.actualUnits && item.actualUnits > 0 ? item.actualUnits : 1;
            const tongTien = item.totalAmount ?? ((item.unitPrice ?? 0) * soLuong);
            const dongia = item.unitPrice ?? (soLuong > 0 ? tongTien / soLuong : tongTien);
            return {
              stt: index + 1,
              hanghoa_loai: 1,
              ma: "",
              ten: item.description ?? DEFAULT_XBILL_PAYLOAD.defaultItemName,
              donvitinh: DEFAULT_XBILL_PAYLOAD.currencyUnit,
              soluong: soLuong,
              dongia: dongia,
              tongtien_chuathue: tongTien,
              mathue: DEFAULT_XBILL_PAYLOAD.itemTaxCode,
              tongtien_cothue: tinhTongTienCoThue(tongTien, -1),
              tyletinhthue: 1,
            };
          });

          const totalCalculated = dschitiet.reduce((sum: number, it: any) => sum + (it.tongtien_chuathue ?? 0), 0);
          const finalTotal = totalCalculated > 0 ? totalCalculated : group.totalAmount;

          const now = new Date();
          const ngaylapFull = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")} ${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}:${String(now.getSeconds()).padStart(2, "0")}`;

          const buyerClass = primaryBill.classYearName || primaryBill.className || "";
          const buyerSchoolYear = primaryBill.schoolYear || "";
          const buyerAddress = buyerClass && buyerSchoolYear
            ? `${buyerClass} - ${buyerSchoolYear}`
            : buyerClass || buyerSchoolYear || "";

          const payload: any = {
            mddkdoanh: commonPayload.mddkdoanh,
            tddkdoanh: commonPayload.tddkdoanh,
            dcddkdoanh: commonPayload.dcddkdoanh,
            mchang: "",
            tchang: "",
            doanhnghiep_mst: commonPayload.doanhnghiep_mst,
            loaihoadon_ma: commonPayload.loaihoadon_ma,
            mauso: commonPayload.mauso,
            kyhieu: commonPayload.kyhieu,
            sophieu: "",
            ma_tracuu: "",
            ma_hoadon: primaryBill.billId || primaryBill.invoidId,
            ngaylap: ngaylapFull,
            tct_macoquan: "",
            dnmua_mst: "",
            dnmua_ten: "",
            dnmua_tennguoimua: primaryBill.studentName ? `${BUYER_NAME_PREFIX} - ${primaryBill.studentName}` : BUYER_NAME_PREFIX,
            dnmua_diachi: buyerAddress,
            dnmua_sdt: "",
            dnmua_email: "",
            dnmua_diachigiaohang: "",
            khachhang_ma: "",
            thanhtoan_phuongthuc: DEFAULT_XBILL_PAYLOAD.paymentMethod,
            thanhtoan_phuongthuc_ten: "",
            thanhtoan_taikhoan: DEFAULT_XBILL_PAYLOAD.bankAccount,
            thanhtoan_nganhang: "",
            tiente_ma: "",
            tygiangoaite: 0,
            thanhtoan_thoihan: "",
            tongtien_chietkhau: 0,
            tongtien_chietkhau_thuongmai: 0,
            ghichu: "",
            tongtien_chuavat: finalTotal,
            tienthue: 0,
            tongtien_covat: finalTotal,
            nguoilap: DEFAULT_XBILL_PAYLOAD.creator,
            dschitiet,
            dsthuesuat: [
              {
                mathue: DEFAULT_XBILL_PAYLOAD.taxCode,
                tongtien_chiuthue: finalTotal,
                tongtien_thue: 0,
              },
            ],
            nghiquyetapdung: 0,
            trinhky: "",
            dnmua_cccd: "",
            hoadonchietkhau: 0,
            sobangke: "",
            ngaybangke: "",
          };

          if (subBillIds.length > 1) {
            payload.subBillIds = subBillIds;
          }

          const res = await postXBillWithAutoRefresh("/token/4", payload, tokenHolder);
          if (res?._silentRefreshFailed) {
            errorCount += groupedByStudent.length - i;
            break;
          }

          const maketqua = res?.info?.result?.maketqua;
          if (res?.code === 200 && (maketqua === XBILL_SUCCESS_CODE || res?.msg === "Success")) {
            successCount++;
            totalBillsProcessed += group.bills.length;
          } else {
            errorCount++;
            console.error(
              `Lỗi HĐ gộp HS ${primaryBill.studentName} (${primaryBill.invoidId}):`,
              res?.info?.result?.motaketqua || res?.msg || "Không tạo được hóa đơn",
            );
          }
        } catch (err) {
          errorCount++;
          console.error("Error issuing consolidated invoice:", err);
        }
      }

      showBulkExportSummary(
        successCount,
        errorCount,
        groupedByStudent.length,
      );

      if (successCount > 0) {
        toast.success(
          `Đã phát hành thành công ${successCount} HĐĐT gộp cho ${totalBillsProcessed} khoản thu (tiết kiệm được ${Math.max(0, totalBillsProcessed - successCount)} số hóa đơn).`,
          { autoClose: 6000 }
        );
      }

      fetchBillData();
      fetchUnpaidBills();
      setSelectedRowKeys([]);
      setIsConsolidatedModalOpen(false);
    } catch (e) {
      console.error("Consolidated export failed:", e);
      toast.error("Có lỗi xảy ra trong quá trình gộp và xuất hóa đơn");
    } finally {
      setIsConsolidating(false);
      setConsolidateProgress(null);
      setProcessingRowKeys([]);
    }
  };

  const formatDateTimeDisplay = useCallback((text?: string | null): string => {
    if (!text) return "";
    const parts = text.split(" ");
    return parts.length === 2
      ? `${parts[1]}  ${parts[0].split("-")[2]}/${parts[0].split("-")[1]}/${parts[0].split("-")[0]}`
      : text;
  }, []);

  const renderInvoiceStatusBadge = useCallback(
    (status: number, isEinvoice?: boolean) => {
      if (isEinvoice === false) {
        return (
          <span style={{ color: "#8c8c8c", fontStyle: "italic" }}>
            Không xuất
          </span>
        );
      }
      const statusMap: { [key: number]: { text: string; color: string } } = {
        0: { text: "Mới tạo", color: "#8c8c8c" },
        1: { text: "Chờ ký", color: "#faad14" },
        2: { text: "Đã cấp số", color: "#1890ff" },
        3: { text: "Đã ký", color: "#52c41a" },
        4: { text: "Ký lỗi", color: "#ff4d4f" },
        5: { text: "Đang gửi TCT", color: "#13c2c2" },
        6: { text: "TVAN đã nhận", color: "#722ed1" },
        7: { text: "TVAN từ chối", color: "#eb2f96" },
        8: { text: "TCT chấp nhận", color: "#2f54eb" },
        9: { text: "TCT từ chối", color: "#f5222d" },
      };
      const config = statusMap[status] || {
        text: "Chưa xuất",
        color: "#d9d9d9",
      };
      return (
        <span style={{ color: config.color, fontWeight: "bold" }}>
          {config.text}
        </span>
      );
    },
    []
  );

  const openBillDetailModal = useCallback((studentInvoiceId: number) => {
    setSelectedStudentInvoiceId(studentInvoiceId);
    setHasChanged(false);
    setIsDetailModalOpen(true);
  }, []);

  const masterColumns: TableProps<StudentBillGroup<unPaidBillDataType>>["columns"] = useMemo(
    () => [
      {
        title: (
          <Checkbox
            checked={isPageAllChecked}
            indeterminate={isPageIndeterminate}
            disabled={allPageSelectableIds.length === 0}
            onChange={handleToggleSelectAllPage}
          />
        ),
        key: "selection",
        width: 48,
        align: "center",
        className: "cell-center",
        render: (_: any, group: StudentBillGroup<unPaidBillDataType>) => {
          const sel = getGroupSelectionState(group.bills, selectedRowKeys, isBillSelectable);
          return (
            <Checkbox
              checked={sel.checked}
              indeterminate={sel.indeterminate}
              disabled={sel.disabled}
              onClick={(e) => e.stopPropagation()}
              onChange={() =>
                setSelectedRowKeys((prev) =>
                  toggleGroupSelectionKeys(group.bills, prev, isBillSelectable)
                )
              }
            />
          );
        },
      },
      {
        title: "Mã biên lai",
        key: "invoidId",
        align: "center",
        className: "cell-center",
        width: 130,
        ellipsis: {
          showTitle: false,
        },
        render: (_: any, group: StudentBillGroup<unPaidBillDataType>) => {
          if (group.billCount === 1) {
            const record = group.bills[0];
            const displayBillId = record.billId || record.invoidId;
            const isConsolidatedChild =
              record.billId && record.invoidId && record.billId !== record.invoidId;
            return (
              <div>
                <EllipsisTooltip text={displayBillId} />
                {isConsolidatedChild && (
                  <Tooltip title={`Đã gộp vào HĐĐT: ${record.invoidId}`}>
                    <div style={{ fontSize: 11, color: "#722ed1", fontWeight: 500 }}>
                      HĐ: {record.invoidId}
                    </div>
                  </Tooltip>
                )}
              </div>
            );
          }

          const billCodes = group.bills
            .map((b) => b.billId || b.invoidId)
            .filter(Boolean)
            .join(", ");
          const distinctInvoices = Array.from(
            new Set(group.bills.map((b) => b.invoidId).filter(Boolean))
          );
          const sharedConsolidatedInvoice =
            distinctInvoices.length === 1 &&
              group.bills.some((b) => b.billId && b.billId !== b.invoidId)
              ? distinctInvoices[0]
              : null;

          return (
            <div>
              <Tooltip title={billCodes ? `Mã biên lai: ${billCodes}` : undefined}>
                <span>{group.billCount} biên lai</span>
              </Tooltip>
              {sharedConsolidatedInvoice && (
                <Tooltip title={`Cả ${group.billCount} khoản đã gộp chung vào HĐĐT: ${sharedConsolidatedInvoice}`}>
                  <div style={{ fontSize: 11, color: "#722ed1", fontWeight: 500, marginTop: 2 }}>
                    HĐ: {sharedConsolidatedInvoice}
                  </div>
                </Tooltip>
              )}
            </div>
          );
        },
      },
      {
        title: "CCCD",
        dataIndex: "studentCode",
        key: "studentCode",
        align: "center",
        className: "cell-center",
        width: 125,
        ellipsis: {
          showTitle: false,
        },
        render: (text: string) => <EllipsisTooltip text={text} />,
      },
      {
        title: "Tên học sinh",
        dataIndex: "studentName",
        key: "studentName",
        align: "left",
        width: 160,
        ellipsis: {
          showTitle: false,
        },
        render: (text: string) => <EllipsisTooltip text={text} />,
      },
      {
        title: "Lớp học",
        dataIndex: "className",
        key: "className",
        render: (_: any, group: StudentBillGroup<unPaidBillDataType>) => (
          <EllipsisTooltip text={group?.className || ""} />
        ),
        align: "center",
        className: "cell-center",
        width: 80,
        ellipsis: {
          showTitle: false,
        },
      },
      {
        title: "Số tiền",
        dataIndex: "totalAmount",
        key: "totalAmount",
        render: (totalAmount: number) => (
          <div className="accounting-amount">
            <EllipsisTooltip text={formatCurrency(totalAmount)} />
          </div>
        ),
        align: "right",
        width: 120,
        ellipsis: {
          showTitle: false,
        },
      },
      {
        title: "Ngày thanh toán",
        key: "paymentDate",
        render: (_: any, group: StudentBillGroup<unPaidBillDataType>) => {
          const formatted = formatDateTimeDisplay(group.latestPaymentDate);
          return <EllipsisTooltip text={formatted} />;
        },
        align: "center",
        className: "cell-center",
        width: 165,
        ellipsis: {
          showTitle: false,
        },
      },
      {
        title: "Nội dung",
        dataIndex: "contentSummary",
        key: "content",
        align: "left",
        ellipsis: {
          showTitle: false,
        },
        render: (contentSummary: string) => (
          <EllipsisTooltip text={contentSummary} />
        ),
      },
      {
        title: "Loại HĐ",
        key: "isEinvoice",
        render: (_: any, group: StudentBillGroup<unPaidBillDataType>) => {
          const einvoiceCount = group.bills.filter((b) => b.isEinvoice !== false).length;
          const internalCount = group.billCount - einvoiceCount;
          if (einvoiceCount > 0 && internalCount === 0) {
            return <Tag color="blue">HĐĐT</Tag>;
          }
          if (einvoiceCount === 0) {
            return <Tag color="default">Nội bộ</Tag>;
          }
          return (
            <Tooltip title={`${einvoiceCount} biên lai HĐĐT, ${internalCount} biên lai Nội bộ`}>
              <Tag color="cyan">
                {einvoiceCount} HĐĐT / {internalCount} NB
              </Tag>
            </Tooltip>
          );
        },
        align: "center",
        className: "cell-center",
        width: 95,
      },
      {
        title: "Trạng thái HĐ",
        key: "invoiceStatus",
        render: (_: any, group: StudentBillGroup<unPaidBillDataType>) => {
          if (group.billCount === 1) {
            const singleBill = group.bills[0];
            return renderInvoiceStatusBadge(
              singleBill.invoiceStatus || 0,
              singleBill.isEinvoice
            );
          }
          const einvoiceBills = group.bills.filter((b) => b.isEinvoice !== false);
          if (einvoiceBills.length === 0) {
            return renderInvoiceStatusBadge(0, false);
          }
          const distinctStatuses = Array.from(
            new Set(einvoiceBills.map((b) => b.invoiceStatus || 0))
          );
          if (distinctStatuses.length === 1) {
            return renderInvoiceStatusBadge(distinctStatuses[0], true);
          }
          const pendingCount = einvoiceBills.filter((b) =>
            EXPORTABLE_INVOICE_STATUSES.includes(b.invoiceStatus || 0)
          ).length;
          const issuedCount = einvoiceBills.length - pendingCount;
          return (
            <Tooltip title={`${issuedCount} đã xuất, ${pendingCount} chờ phát hành`}>
              <span style={{ fontSize: 12, fontWeight: 600, color: "#fa8c16" }}>
                {issuedCount}/{einvoiceBills.length} đã xuất
              </span>
            </Tooltip>
          );
        },
        align: "center",
        className: "cell-center",
        width: 115,
      },
      {
        title: "Ngày xuất",
        key: "exportDate",
        render: (_: any, group: StudentBillGroup<unPaidBillDataType>) => (
          <EllipsisTooltip text={formatDateTimeDisplay(group.latestExportDate)} />
        ),
        align: "center",
        className: "cell-center",
        width: 165,
        ellipsis: {
          showTitle: false,
        },
      },
      {
        title: "Hành động",
        key: "action",
        render: (_: any, group: StudentBillGroup<unPaidBillDataType>) => {
          if (group.billCount === 1) {
            return (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Button
                  type="link"
                  onClick={() => openBillDetailModal(group.bills[0].studentInvoiceId)}
                >
                  Chi tiết
                </Button>
              </div>
            );
          }

          return <span style={{ color: "#bfbfbf" }}>—</span>;
        },
        align: "center",
        className: "cell-center",
        width: 95,
      },
    ],
    [
      isPageAllChecked,
      isPageIndeterminate,
      allPageSelectableIds.length,
      handleToggleSelectAllPage,
      selectedRowKeys,
      isBillSelectable,
      formatDateTimeDisplay,
      renderInvoiceStatusBadge,
      openBillDetailModal,
    ]
  );

  const renderExpandedPaidBills = useCallback(
    (group: StudentBillGroup<unPaidBillDataType>) => {
      const subColumns: TableProps<unPaidBillDataType>["columns"] = [
        {
          title: "Mã biên lai",
          dataIndex: "invoidId",
          key: "invoidId",
          width: 150,
          align: "center",
          className: "cell-center",
          render: (text: string, record: unPaidBillDataType) => {
            const displayBillId = record.billId || text;
            const isConsolidatedChild =
              record.billId && record.invoidId && record.billId !== record.invoidId;
            return (
              <div>
                <EllipsisTooltip text={displayBillId} />
                {isConsolidatedChild && (
                  <Tooltip title={`Đã gộp vào HĐĐT: ${record.invoidId}`}>
                    <div style={{ fontSize: 11, color: "#722ed1", fontWeight: 500, marginTop: 2 }}>
                      HĐ: {record.invoidId}
                    </div>
                  </Tooltip>
                )}
              </div>
            );
          },
        },
        {
          title: "Nội dung khoản thu",
          dataIndex: "content",
          key: "content",
          align: "left",
          ellipsis: { showTitle: false },
          render: (content: string) => <EllipsisTooltip text={content} />,
        },
        {
          title: "Số tiền",
          dataIndex: "cost",
          key: "cost",
          width: 130,
          align: "right",
          render: (cost: number) => (
            <div className="accounting-amount">{formatCurrency(cost)}</div>
          ),
        },
        {
          title: "Ngày thanh toán",
          dataIndex: "paymentDate",
          key: "paymentDate",
          width: 165,
          align: "center",
          className: "cell-center",
          render: (text: string) => (
            <EllipsisTooltip text={formatDateTimeDisplay(text)} />
          ),
        },
        {
          title: "Loại HĐ",
          dataIndex: "isEinvoice",
          key: "isEinvoice",
          width: 90,
          align: "center",
          className: "cell-center",
          render: (_: any, record: unPaidBillDataType) =>
            record.isEinvoice !== false ? (
              <Tag color="blue">HĐĐT</Tag>
            ) : (
              <Tag color="default">Nội bộ</Tag>
            ),
        },
        {
          title: "Trạng thái HĐ",
          dataIndex: "invoiceStatus",
          key: "invoiceStatus",
          width: 120,
          align: "center",
          className: "cell-center",
          render: (status: number, record: unPaidBillDataType) =>
            renderInvoiceStatusBadge(status, record.isEinvoice),
        },
        {
          title: "Ngày xuất",
          dataIndex: "exportDate",
          key: "exportDate",
          width: 165,
          align: "center",
          className: "cell-center",
          render: (text: string) => (
            <EllipsisTooltip text={formatDateTimeDisplay(text)} />
          ),
        },
        {
          title: "Hành động",
          key: "action",
          width: 100,
          align: "center",
          className: "cell-center",
          render: (_: any, record: unPaidBillDataType) => (
            <Button
              type="link"
              size="small"
              onClick={() => openBillDetailModal(record.studentInvoiceId)}
            >
              Chi tiết
            </Button>
          ),
        },
      ];

      return (
        <div className="student-subtable-container">
          <Table<unPaidBillDataType>
            size="small"
            bordered
            pagination={false}
            rowKey="studentInvoiceId"
            columns={subColumns}
            dataSource={group.bills}
            rowSelection={{
              selectedRowKeys,
              onSelect: (record, selected) => {
                setSelectedRowKeys((prev) =>
                  toggleSingleBillKey(record.studentInvoiceId, selected, prev)
                );
              },
              hideSelectAll: true,
              getCheckboxProps: (record: unPaidBillDataType) => ({
                disabled: !isBillSelectable(record),
              }),
            }}
          />
        </div>
      );
    },
    [
      selectedRowKeys,
      isBillSelectable,
      formatDateTimeDisplay,
      renderInvoiceStatusBadge,
      openBillDetailModal,
    ]
  );

  const exportableBillsCount = unPaidBillList.filter(
    (item) =>
      selectedRowKeys.includes(item.studentInvoiceId) &&
      item.isEinvoice !== false &&
      EXPORTABLE_INVOICE_STATUSES.includes(item.invoiceStatus || 0),
  ).length;
  const checkableBillsCount = unPaidBillList.filter(
    (item) =>
      selectedRowKeys.includes(item.studentInvoiceId) &&
      item.isEinvoice !== false &&
      (item.invoiceStatus || 0) >= ISSUED_INVOICE_MIN_STATUS,
  ).length;

  const groupedByStudent = useMemo(() => {
    const map = new Map<number, unPaidBillDataType[]>();
    const exportableBills = unPaidBillList.filter(
      (item) =>
        selectedRowKeys.includes(item.studentInvoiceId) &&
        item.isEinvoice !== false &&
        EXPORTABLE_INVOICE_STATUSES.includes(item.invoiceStatus || 0),
    );
    for (const bill of exportableBills) {
      const list = map.get(bill.studentId) || [];
      list.push(bill);
      map.set(bill.studentId, list);
    }
    return Array.from(map.entries()).map(([studentId, rawBills]) => {
      const bills = [...rawBills].sort((a, b) => {
        const aIsMasterDraft = a.billId && a.invoidId && a.billId === a.invoidId && (a.invoiceStatus || 0) > 0 ? 0 : 1;
        const bIsMasterDraft = b.billId && b.invoidId && b.billId === b.invoidId && (b.invoiceStatus || 0) > 0 ? 0 : 1;
        if (aIsMasterDraft !== bIsMasterDraft) {
          return aIsMasterDraft - bIsMasterDraft;
        }
        return (a.studentInvoiceId || 0) - (b.studentInvoiceId || 0);
      });
      return {
        studentId,
        studentName: bills[0]?.studentName || "Học sinh",
        studentCode: bills[0]?.studentCode || "",
        className: bills[0]?.className || bills[0]?.classYearName || "",
        bills,
        totalAmount: bills.reduce((sum, b) => sum + (b.cost || 0), 0),
        billCount: bills.length,
      };
    });
  }, [unPaidBillList, selectedRowKeys]);

  return (
    <div>
      <Row
        gutter={8}
        style={{ marginBottom: "16px", display: "flex", alignItems: "center" }}
      >
        <Col>
          <Select
            placeholder="Năm"
            value={selectedYear}
            onChange={(value) => {
              setSelectedYear(value);
              setSelectedClass(undefined);
              setCurrentPage(1);
            }}
            style={{ width: 120, marginRight: 8 }}
          >
            {years.map((year) => (
              <Option key={year} value={year}>
                {year}
              </Option>
            ))}
          </Select>
        </Col>
        <Col>
          <Select
            placeholder="Tháng"
            value={selectedMonth}
            onChange={(value) => {
              setSelectedMonth(value);
              setCurrentPage(1);
            }}
            style={{ width: 120 }}
          >
            {months.map((month) => (
              <Option key={month.value} value={month.value}>
                {month.label}
              </Option>
            ))}
          </Select>
        </Col>
        <Col>
          {selectedMonth === getStoredWorkMonth().month && selectedYear === getStoredWorkMonth().year ? (
            <Tag color="blue" style={{ height: 32, display: 'inline-flex', alignItems: 'center', gap: 4, fontWeight: 500, margin: 0 }}>
              <CalendarOutlined /> Kỳ làm việc
            </Tag>
          ) : (
            <Tooltip title="Đang xem kỳ khác. Bấm để quay về Kỳ làm việc trên thanh Header">
              <Button
                size="middle"
                icon={<SyncOutlined />}
                onClick={() => {
                  const wm = getStoredWorkMonth();
                  setSelectedMonth(wm.month);
                  setSelectedYear(wm.year);
                  setCurrentPage(1);
                }}
              >
                Về kỳ T{getStoredWorkMonth().month}/{getStoredWorkMonth().year}
              </Button>
            </Tooltip>
          )}
        </Col>
        <Col>
          <ClassLevelSelect
            value={selectedClassLevel}
            onChange={(val) => {
              setSelectedClassLevel(val);
              setSelectedClass(undefined);
              setCurrentPage(1);
            }}
            placeholder="Tất cả khối"
            style={{ width: 130 }}
          />
        </Col>
        <Col>
          <Select
            showSearch
            allowClear
            placeholder="Lớp học"
            value={selectedClass}
            onChange={(value) => {
              setSelectedClass(value);
              setCurrentPage(1);
            }}
            style={{ width: 150 }}
            filterOption={(input, option) =>
              (option?.children as unknown as string)
                ?.toLowerCase()
                .includes(input.toLowerCase())
            }
          >
            {classList
              .filter((cls) => !selectedClassLevel || String(cls.classLevel).trim() === selectedClassLevel.trim())
              .map((cls) => (
                <Option key={cls.classYearId} value={cls.classYearId}>
                  {cls.className}
                </Option>
              ))}
          </Select>
        </Col>
        <Col>
          <Input
            placeholder="Tìm tên học sinh"
            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
            allowClear
            value={searchStudentName}
            onChange={(e) => setSearchStudentName(e.target.value)}
            onPressEnter={handlePressEnter}
            style={{ width: 180 }}
          />
        </Col>
        <Col>
          <Select
            value={einvoiceFilter}
            onChange={(val) => {
              setEinvoiceFilter(val);
              if (val === 'no_einvoice') {
                setSelectedInvoiceStatus(undefined);
              }
              if (selectedIncomeSchoolId !== undefined) {
                const currentSrc = incomeSchoolList.find((s) => s.id === selectedIncomeSchoolId);
                if (
                  currentSrc &&
                  ((val === 'einvoice' && !currentSrc.isEinvoice) ||
                    (val === 'no_einvoice' && Boolean(currentSrc.isEinvoice)))
                ) {
                  setSelectedIncomeSchoolId(undefined);
                }
              }
              setCurrentPage(1);
            }}
            style={{ width: 175 }}
          >
            <Option value="einvoice">
              <span style={{ color: "#1890ff", fontWeight: 500 }}>● Cần xuất HĐĐT</span>
            </Option>
            <Option value="no_einvoice">
              <span style={{ color: "#8c8c8c", fontWeight: 500 }}>○ Không xuất HĐĐT</span>
            </Option>
            <Option value="all">
              <span style={{ fontWeight: 500 }}>Tất cả biên lai</span>
            </Option>
          </Select>
        </Col>
        <Col>
          <Select
            showSearch
            allowClear
            placeholder="Tất cả nguồn thu"
            value={selectedIncomeSchoolId}
            onChange={(value) => {
              setSelectedIncomeSchoolId(value);
              setCurrentPage(1);
            }}
            style={{ width: 180 }}
            filterOption={(input, option) =>
              (option?.children as unknown as string)
                ?.toLowerCase()
                .includes(input.toLowerCase())
            }
          >
            {filteredIncomeSchools.map((src) => (
              <Option key={src.id} value={src.id}>
                {src.name}
              </Option>
            ))}
          </Select>
        </Col>
        <Col>
          <Select
            placeholder="Trạng thái HĐ"
            allowClear
            value={einvoiceFilter === 'no_einvoice' ? undefined : selectedInvoiceStatus}
            disabled={einvoiceFilter === 'no_einvoice'}
            onChange={(val) => {
              setSelectedInvoiceStatus(val);
              setCurrentPage(1);
            }}
            style={{ width: 185 }}
          >
            <Option value={0}>
              <span style={{ color: "#8c8c8c", fontWeight: 500 }}>Chưa xuất (Mới tạo)</span>
            </Option>
            <Option value={1}>
              <span style={{ color: "#faad14", fontWeight: 500 }}>Chờ ký</span>
            </Option>
            <Option value={2}>
              <span style={{ color: "#1890ff", fontWeight: 500 }}>Đã cấp số</span>
            </Option>
            <Option value={3}>
              <span style={{ color: "#52c41a", fontWeight: 500 }}>Đã ký</span>
            </Option>
            <Option value={4}>
              <span style={{ color: "#ff4d4f", fontWeight: 500 }}>Ký lỗi</span>
            </Option>
            <Option value={5}>
              <span style={{ color: "#13c2c2", fontWeight: 500 }}>Đang gửi TCT</span>
            </Option>
            <Option value={6}>
              <span style={{ color: "#722ed1", fontWeight: 500 }}>TVAN đã nhận</span>
            </Option>
            <Option value={7}>
              <span style={{ color: "#eb2f96", fontWeight: 500 }}>TVAN từ chối</span>
            </Option>
            <Option value={8}>
              <span style={{ color: "#2f54eb", fontWeight: 500 }}>TCT chấp nhận</span>
            </Option>
            <Option value={9}>
              <span style={{ color: "#f5222d", fontWeight: 500 }}>TCT từ chối</span>
            </Option>
          </Select>
        </Col>
        <Col flex="auto" style={{ textAlign: "right" }}>
          <Space size={8}>
            {multiBillGroupKeys.length > 0 && (
              <Button onClick={handleToggleExpandAll}>
                {isAllExpanded
                  ? "Thu gọn chi tiết"
                  : `Mở rộng chi tiết (${multiBillGroupKeys.length} HS)`}
              </Button>
            )}
            {remainingInvoiceCount !== null && (
              <Tag
                color={
                  remainingInvoiceCount > 10
                    ? "blue"
                    : remainingInvoiceCount > 0
                      ? "orange"
                      : "red"
                }
                style={{ margin: 0 }}
              >
                Số hóa đơn có thể xuất: <strong>{remainingInvoiceCount}/{totalInvoiceCount || 0}</strong>
              </Tag>
            )}
          </Space>
        </Col>
      </Row>
      {selectedRowKeys.length > 0 && (
        <Alert
          message={
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "16px" }}
              >
                <Typography.Text style={{ fontSize: "15px" }}>
                  Đã chọn:{" "}
                  <Typography.Text strong style={{ color: "#1890ff" }}>
                    {selectedRowKeys.length} biên lai
                  </Typography.Text>
                  {exportableBillsCount > 0 && (
                    <span style={{ fontSize: "13px", color: "#52c41a", marginLeft: "6px", fontWeight: 500 }}>
                      ({exportableBillsCount} chờ phát hành)
                    </span>
                  )}
                  {checkableBillsCount > 0 && (
                    <span style={{ fontSize: "13px", color: "#1890ff", marginLeft: "6px", fontWeight: 500 }}>
                      ({checkableBillsCount} đã phát hành)
                    </span>
                  )}
                </Typography.Text>
                <div
                  style={{
                    width: "1px",
                    height: "16px",
                    backgroundColor: "#d9d9d9",
                  }}
                />
                <Typography.Text style={{ fontSize: "15px" }}>
                  Tổng tiền:{" "}
                  <Typography.Text
                    strong
                    style={{ fontSize: "18px", color: "#f5222d" }}
                  >
                    {formatCurrency(totalSelectedAmount)}
                  </Typography.Text>
                </Typography.Text>
              </div>
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <Button
                  type="primary"
                  icon={<ThunderboltOutlined />}
                  onClick={() => {
                    if (exportableBillsCount === 0 || groupedByStudent.length === 0) return;
                    if (remainingInvoiceCount === null || remainingInvoiceCount === 0) {
                      toast.error("Trường đã hết hóa đơn khả dụng. Vui lòng mua thêm.");
                      return;
                    }
                    if (groupedByStudent.length > remainingInvoiceCount) {
                      toast.error(
                        `Cần xuất ${groupedByStudent.length} hóa đơn cho ${groupedByStudent.length} học sinh, nhưng trường chỉ còn ${remainingInvoiceCount} hóa đơn khả dụng. Vui lòng mua thêm hóa đơn.`,
                      );
                      return;
                    }
                    const status = localStorage.getItem("xbill_status") || sessionStorage.getItem("xbill_status");
                    if (status !== "LOGGED_IN") {
                      toast.error("Bạn chưa đăng nhập XBill. Vui lòng vào màn 'Thông tin cấu hình hóa đơn' để đăng nhập trước khi thao tác!");
                      return;
                    }
                    setIsConsolidatedModalOpen(true);
                  }}
                  disabled={exportableBillsCount === 0 || processingRowKeys.length > 0}
                  loading={processingRowKeys.length > 0}
                  style={{
                    backgroundColor: exportableBillsCount === 0 ? "#f5f5f5" : "#6366f1",
                    borderColor: exportableBillsCount === 0 ? "#d9d9d9" : "#6366f1",
                    color: exportableBillsCount === 0 ? "rgba(0, 0, 0, 0.25)" : "#fff",
                    fontWeight: "600",
                    height: "40px",
                    borderRadius: "8px",
                    padding: "0 18px",
                    boxShadow: exportableBillsCount === 0 ? "none" : "0 4px 12px rgba(99, 102, 241, 0.25)",
                    transition: "all 0.3s",
                  }}
                >
                  Gộp & Xuất HĐĐT ({groupedByStudent.length} HS)
                </Button>
                <Button
                  type="primary"
                  icon={<SafetyCertificateOutlined />}
                  onClick={() => {
                    if (exportableBillsCount === 0) return;
                    if (remainingInvoiceCount === null || remainingInvoiceCount === 0) {
                      toast.error("Trường đã hết hóa đơn khả dụng. Vui lòng mua thêm.");
                      return;
                    }
                    if (exportableBillsCount > remainingInvoiceCount) {
                      toast.error(
                        `Bạn đang chọn ${exportableBillsCount} hóa đơn, nhưng chỉ còn lại ${remainingInvoiceCount} hóa đơn khả dụng. Vui lòng bỏ chọn bớt hoặc mua thêm hóa đơn.`,
                      );
                      return;
                    }
                    const status = localStorage.getItem("xbill_status") || sessionStorage.getItem("xbill_status");
                    if (status !== "LOGGED_IN") {
                      toast.error("Bạn chưa đăng nhập XBill. Vui lòng vào màn 'Thông tin cấu hình hóa đơn' để đăng nhập trước khi thao tác!");
                      return;
                    }
                    setIsConfirmIssueModalOpen(true);
                  }}
                  disabled={exportableBillsCount === 0 || processingRowKeys.length > 0}
                  loading={processingRowKeys.length > 0}
                  style={{
                    backgroundColor:
                      exportableBillsCount === 0 ? "#f5f5f5" : "#10b981",
                    borderColor:
                      exportableBillsCount === 0 ? "#d9d9d9" : "#10b981",
                    color:
                      exportableBillsCount === 0
                        ? "rgba(0, 0, 0, 0.25)"
                        : "#fff",
                    fontWeight: "600",
                    height: "40px",
                    borderRadius: "8px",
                    padding: "0 20px",
                    boxShadow:
                      exportableBillsCount === 0
                        ? "none"
                        : "0 4px 12px rgba(16, 185, 129, 0.2)",
                    transition: "all 0.3s",
                  }}
                >
                  Phát hành HĐ HSM ({exportableBillsCount})
                </Button>
                <Button
                  type="primary"
                  icon={<SyncOutlined />}
                  onClick={() => handleBulkAction("CHECK")}
                  disabled={checkableBillsCount === 0}
                  style={{
                    backgroundColor:
                      checkableBillsCount === 0 ? "#f5f5f5" : "#3b82f6",
                    borderColor:
                      checkableBillsCount === 0 ? "#d9d9d9" : "#3b82f6",
                    color:
                      checkableBillsCount === 0
                        ? "rgba(0, 0, 0, 0.25)"
                        : "#fff",
                    fontWeight: "600",
                    height: "40px",
                    borderRadius: "8px",
                    padding: "0 20px",
                    boxShadow:
                      checkableBillsCount === 0
                        ? "none"
                        : "0 4px 12px rgba(59, 130, 246, 0.2)",
                    transition: "all 0.3s",
                  }}
                >
                  Đồng bộ trạng thái CQT ({checkableBillsCount})
                </Button>
              </div>
            </div>
          }
          type="info"
          showIcon
          style={{
            marginBottom: 16,
            padding: "8px 16px",
            borderRadius: "8px",
            border: "1px solid #91d5ff",
            backgroundColor: "#e6f7ff",
            boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
          }}
        />
      )}
      <Table<StudentBillGroup<unPaidBillDataType>>
        tableLayout="fixed"
        loading={loadingTable}
        columns={masterColumns}
        dataSource={studentGroups}
        rowKey="groupKey"
        expandable={{
          expandedRowKeys,
          onExpandedRowsChange: (keys) => setExpandedRowKeys([...keys]),
          expandedRowRender: renderExpandedPaidBills,
          rowExpandable: (group) => group.billCount > 1,
        }}
        summary={() => {
          if (studentGroups.length === 0) return null;
          const pageTotal = studentGroups.reduce((sum, g) => sum + g.totalAmount, 0);
          return (
            <Table.Summary.Row className="bill-table-summary-row">
              <Table.Summary.Cell index={0} colSpan={6}>
                Tổng trang hiện tại: {studentGroups.length} học sinh ({unPaidBillList.length} biên lai)
              </Table.Summary.Cell>
              <Table.Summary.Cell index={5} align="right">
                <span className="accounting-amount" style={{ fontWeight: 600 }}>
                  {formatCurrency(pageTotal)}
                </span>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={6} colSpan={6} />
            </Table.Summary.Row>
          );
        }}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: totalItems,
          pageSizeOptions: ["10", "50", "100", "200", "500"],
          showSizeChanger: true,
          showTotal: (total) =>
            `Tổng ${total} học sinh (${unPaidBillList.length} biên lai trên trang)`,
          onChange: handlePageChange,
        }}
      />

      <Modal
        title="Đăng nhập XBill"
        open={isLoginModalOpen}
        onCancel={() => setIsLoginModalOpen(false)}
        onOk={handleXBillLogin}
        confirmLoading={loadingLogin}
        okText="Đăng nhập"
        cancelText="Hủy"
      >
        <Form form={loginForm} layout="vertical" autoComplete="off">
          <Form.Item
            name="mst"
            label="Mã số thuế"
            rules={[{ required: true, message: "Nhập mã số thuế" }]}
          >
            <Input placeholder="Nhập MST" autoComplete="off" />
          </Form.Item>

          <Form.Item
            name="username"
            label="Tài khoản"
            rules={[{ required: true, message: "Nhập tài khoản" }]}
          >
            <Input placeholder="Nhập tài khoản" autoComplete="off" />
          </Form.Item>

          <Form.Item
            name="password"
            label="Mật khẩu"
            rules={[{ required: true, message: "Nhập mật khẩu" }]}
          >
            <Input.Password
              placeholder="Nhập mật khẩu"
              autoComplete="new-password"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal xác nhận phát hành HĐ HSM to rõ ở giữa màn hình */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 12, paddingBottom: 6 }}>
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: "50%",
                backgroundColor: "#ecfdf5",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <SafetyCertificateFilled style={{ color: "#10b981", fontSize: 26 }} />
            </div>
            <div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#111827", lineHeight: 1.3 }}>
                Xác nhận phát hành Hóa đơn HSM
              </div>
              <div style={{ fontSize: 13, color: "#6b7280", fontWeight: 400, marginTop: 2 }}>
                Ký số tập trung và gửi hóa đơn lên Cơ quan Thuế
              </div>
            </div>
          </div>
        }
        open={isConfirmIssueModalOpen}
        centered
        width={540}
        onCancel={() => setIsConfirmIssueModalOpen(false)}
        footer={[
          <Button
            key="cancel"
            size="large"
            onClick={() => setIsConfirmIssueModalOpen(false)}
            style={{ borderRadius: 8, height: 42, padding: "0 24px", fontWeight: 500 }}
          >
            Hủy bỏ
          </Button>,
          <Button
            key="submit"
            type="primary"
            size="large"
            icon={<SafetyCertificateOutlined />}
            loading={processingRowKeys.length > 0}
            onClick={() => {
              setIsConfirmIssueModalOpen(false);
              handleBulkAction("EXPORT");
            }}
            style={{
              backgroundColor: "#10b981",
              borderColor: "#10b981",
              borderRadius: 8,
              height: 42,
              padding: "0 24px",
              fontWeight: 600,
              boxShadow: "0 4px 12px rgba(16, 185, 129, 0.3)",
            }}
          >
            Xác nhận phát hành ({exportableBillsCount})
          </Button>,
        ]}
      >
        <div style={{ padding: "16px 0 8px" }}>
          {/* Box tóm tắt thông tin phát hành */}
          <div
            style={{
              backgroundColor: "#f9fafb",
              border: "1px solid #e5e7eb",
              borderRadius: 12,
              padding: "16px 20px",
              marginBottom: 16,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, fontSize: 15 }}>
              <span style={{ color: "#4b5563" }}>Số lượng hóa đơn phát hành:</span>
              <strong style={{ color: "#10b981", fontSize: 17, fontWeight: 700 }}>
                {exportableBillsCount} hóa đơn
              </strong>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, fontSize: 15 }}>
              <span style={{ color: "#4b5563" }}>Tổng số tiền:</span>
              <strong style={{ color: "#f5222d", fontSize: 18, fontWeight: 700 }}>
                {formatCurrency(totalSelectedAmount)}
              </strong>
            </div>

            <div style={{ borderTop: "1px dashed #e5e7eb", margin: "10px 0" }} />

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 14 }}>
              <span style={{ color: "#4b5563" }}>Hạn ngạch hóa đơn trường:</span>
              <span style={{ color: "#1f2937" }}>
                Còn lại <b>{remainingInvoiceCount ?? 0}</b> lượt
                {remainingInvoiceCount !== null && (
                  <span style={{ color: "#6b7280", fontSize: 13, marginLeft: 6 }}>
                    (sau phát hành còn: <b>{Math.max(0, remainingInvoiceCount - exportableBillsCount)}</b> lượt)
                  </span>
                )}
              </span>
            </div>
          </div>

          {/* Cảnh báo pháp lý */}
          <div
            style={{
              backgroundColor: "#fffbe6",
              border: "1px solid #ffe58f",
              borderRadius: 10,
              padding: "12px 16px",
              fontSize: 13,
              color: "#d48806",
              lineHeight: 1.6,
            }}
          >
            <strong>Lưu ý:</strong> Hệ thống sẽ cấp số, đóng dấu chữ ký số HSM và gửi trực tiếp dữ liệu hóa đơn sang Tổng cục Thuế. Thao tác này <strong>không thể thu hồi</strong> và sẽ trừ trực tiếp hạn ngạch hóa đơn khả dụng của trường.
          </div>
        </div>
      </Modal>

      {/* Modal xác nhận Gộp & Phát hành HĐĐT theo học sinh */}
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 12, paddingBottom: 6 }}>
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: "50%",
                backgroundColor: "#eef2ff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <ThunderboltOutlined style={{ color: "#6366f1", fontSize: 24 }} />
            </div>
            <div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#111827", lineHeight: 1.3 }}>
                Xác nhận Gộp & Phát hành Hóa đơn điện tử theo học sinh
              </div>
              <div style={{ fontSize: 13, color: "#6b7280", fontWeight: 400, marginTop: 2 }}>
                Gộp nhiều khoản thu trong tháng của từng học sinh thành 1 hóa đơn duy nhất gửi CQT
              </div>
            </div>
          </div>
        }
        open={isConsolidatedModalOpen}
        centered
        width={720}
        onCancel={() => !isConsolidating && setIsConsolidatedModalOpen(false)}
        footer={[
          <Button
            key="cancel"
            size="large"
            disabled={isConsolidating}
            onClick={() => setIsConsolidatedModalOpen(false)}
            style={{ borderRadius: 8, height: 42, padding: "0 24px", fontWeight: 500 }}
          >
            Hủy bỏ
          </Button>,
          <Button
            key="submit"
            type="primary"
            size="large"
            icon={<ThunderboltOutlined />}
            loading={isConsolidating}
            onClick={handleConsolidatedExport}
            style={{
              backgroundColor: "#6366f1",
              borderColor: "#6366f1",
              borderRadius: 8,
              height: 42,
              padding: "0 24px",
              fontWeight: 600,
              boxShadow: "0 4px 14px rgba(99, 102, 241, 0.35)",
            }}
          >
            {isConsolidating && consolidateProgress
              ? `Đang xuất (${consolidateProgress.current}/${consolidateProgress.total})...`
              : `Xác nhận Gộp & Xuất (${groupedByStudent.length} HĐ)`}
          </Button>,
        ]}
      >
        <div style={{ padding: "8px 0" }}>
          {/* Banner thống kê tiết kiệm */}
          <div
            style={{
              backgroundColor: "#f5f3ff",
              border: "1px solid #ddd6fe",
              borderRadius: 10,
              padding: "16px 20px",
              marginBottom: 16,
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: 16,
              textAlign: "center",
            }}
          >
            <div>
              <div style={{ fontSize: 12, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5 }}>
                Khoản thu đã chọn
              </div>
              <div style={{ fontSize: 22, fontWeight: 700, color: "#1f2937", marginTop: 4 }}>
                {exportableBillsCount}
              </div>
            </div>
            <div style={{ borderLeft: "1px solid #e9d5ff", borderRight: "1px solid #e9d5ff" }}>
              <div style={{ fontSize: 12, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5 }}>
                Số HĐ sẽ phát hành
              </div>
              <div style={{ fontSize: 22, fontWeight: 700, color: "#6366f1", marginTop: 4 }}>
                {groupedByStudent.length}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: "#059669", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>
                Tiết kiệm số HĐ
              </div>
              <div style={{ fontSize: 22, fontWeight: 700, color: "#059669", marginTop: 4 }}>
                +{Math.max(0, exportableBillsCount - groupedByStudent.length)}
              </div>
            </div>
          </div>

          {remainingInvoiceCount !== null && (
            <div
              style={{
                fontSize: 13,
                color: "#4b5563",
                marginBottom: 16,
                backgroundColor: "#f9fafb",
                padding: "8px 14px",
                borderRadius: 6,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span>
                Số HĐ khả dụng của trường: <b>{remainingInvoiceCount}</b>
              </span>
              <span>
                Dự kiến sau khi xuất còn:{" "}
                <b style={{ color: remainingInvoiceCount - groupedByStudent.length >= 0 ? "#10b981" : "#ef4444" }}>
                  {Math.max(0, remainingInvoiceCount - groupedByStudent.length)}
                </b>{" "}
                lượt
              </span>
            </div>
          )}

          {/* Danh sách học sinh và các khoản thu gộp */}
          <div style={{ fontSize: 14, fontWeight: 600, color: "#374151", marginBottom: 8 }}>
            Danh sách học sinh được gộp hóa đơn ({groupedByStudent.length} học sinh):
          </div>
          <div
            style={{
              maxHeight: 280,
              overflowY: "auto",
              border: "1px solid #e5e7eb",
              borderRadius: 8,
              padding: "4px 8px",
            }}
          >
            <Table
              dataSource={groupedByStudent}
              rowKey="studentId"
              pagination={false}
              size="small"
              columns={[
                {
                  title: "Học sinh",
                  key: "student",
                  render: (_, record) => (
                    <div>
                      <div style={{ fontWeight: 600, color: "#111827" }}>{record.studentName}</div>
                      <div style={{ fontSize: 12, color: "#6b7280" }}>
                        {record.studentCode ? `MHS: ${record.studentCode} | ` : ""}{record.className}
                      </div>
                    </div>
                  ),
                },
                {
                  title: "Số khoản thu",
                  key: "billCount",
                  width: 120,
                  align: "center",
                  render: (_, record) => (
                    <Tag color="purple">
                      {record.billCount} khoản thu
                    </Tag>
                  ),
                },
                {
                  title: "Tổng tiền HĐ gộp",
                  key: "totalAmount",
                  width: 150,
                  align: "right",
                  render: (_, record) => (
                    <span style={{ fontWeight: 700, color: "#059669" }}>
                      {formatCurrency(record.totalAmount)}
                    </span>
                  ),
                },
              ]}
              expandable={{
                expandedRowRender: (record) => (
                  <div style={{ margin: "4px 0 4px 16px" }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#4b5563", marginBottom: 4 }}>
                      Chi tiết các khoản thu được gộp:
                    </div>
                    {record.bills.map((b, idx) => (
                      <div
                        key={b.studentInvoiceId}
                        style={{
                          fontSize: 12,
                          color: "#374151",
                          display: "flex",
                          justifyContent: "space-between",
                          padding: "2px 0",
                          borderBottom: idx < record.bills.length - 1 ? "1px dashed #f3f4f6" : "none",
                        }}
                      >
                        <span>
                          • {b.content || b.invoidId} {b.invoidId ? `(${b.invoidId})` : ""}
                        </span>
                        <span style={{ fontWeight: 500 }}>
                          {formatCurrency(b.cost || 0)}
                        </span>
                      </div>
                    ))}
                  </div>
                ),
                rowExpandable: (record) => record.billCount > 1,
              }}
            />
          </div>
        </div>
      </Modal>

      <Modal
        title="Chi tiết biên lai"
        open={isDetailModalOpen}
        onCancel={() => {
          setIsDetailModalOpen(false);
          setSelectedStudentInvoiceId(undefined);
          if (hasChanged) {
            fetchUnpaidBills();
            fetchBillData();
            setHasChanged(false);
          }
        }}
        footer={null}
        width={1500}
        destroyOnClose
      >
        <div style={{ maxHeight: '75vh', overflowY: 'auto' }}>
          {selectedStudentInvoiceId && (
            <BillInfo
              studentInvoiceId={selectedStudentInvoiceId}
              onSuccess={() => setHasChanged(true)}
            />
          )}
        </div>
      </Modal>
    </div>
  );
};
